import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { notificationAPI } from '../services/api';
import toast from 'react-hot-toast';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    const userRole = typeof user?.role === 'object' ? user?.role?.name : user?.role;
    if (!user || !['ADMIN', 'SUPER_ADMIN', 'MANAGER', 'EMPLOYEE'].includes(userRole)) {
      return;
    }

    try {
      const response = await notificationAPI.getNotifications();
      const newNotifications = response.data.notifications || [];
      
      setNotifications(newNotifications);
      setUnreadCount(newNotifications.filter(n => !n.isRead).length);
    } catch (error) {
      // Only log errors, not every fetch
      console.error('Failed to fetch notifications:', error);
    }
  }, [user]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      await notificationAPI.clearAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  // Add new notification (for real-time updates)
  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.isRead) {
      setUnreadCount(prev => prev + 1);
      
      // Show toast notification
      toast.success(
        <div>
          <div className="font-semibold">{notification.title}</div>
          <div className="text-sm text-gray-600">{notification.message}</div>
        </div>,
        {
          duration: 5000,
          position: 'top-right',
        }
      );
    }
  };

  // Optimized: Only fetch notifications when needed, not constantly polling
  useEffect(() => {
    const userRole = typeof user?.role === 'object' ? user?.role?.name : user?.role;
    if (!user || !['ADMIN', 'SUPER_ADMIN', 'MANAGER', 'EMPLOYEE'].includes(userRole)) {
      return;
    }

    // Initial fetch only
    fetchNotifications();

    // Fetch notifications when window regains focus (user comes back to tab)
    const handleFocus = () => {
      fetchNotifications();
    };

    // Fetch notifications when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNotifications();
      }
    };

    // Set up a longer interval as fallback (only when tab is active) - every 3 minutes
    let interval = null;
    const setupInterval = () => {
      if (!document.hidden) {
        interval = setInterval(() => {
          if (!document.hidden) {
            fetchNotifications();
          }
        }, 3 * 60 * 1000); // 3 minutes
      }
    };

    setupInterval();

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, user?.role, fetchNotifications]);

  // Clear notifications when user changes
  useEffect(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, [user?.id]);

  const value = {
    notifications,
    unreadCount,
    isConnected,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    addNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
